import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAgencyDashboardReports,
  fetchUserDivision,
  fetchZonePartiesPOReports,
  fetchZonesPOReports
} from 'Service/ApiService';

const joinSelectedValues = (items = [], key = 'value') =>
  items
    .map((item) => item?.[key] || item?.value || '')
    .filter(Boolean)
    .join(',');

const joinSelectedMonths = (items = []) =>
  items
    .map((item) => String(item?.value || item?.label || '').split('-')[0].split('_').pop())
    .filter(Boolean)
    .join(',');

const AgencyPerformanceReportController = ({
  area = [],
  customer = [],
  month = [],
  division = [],
  dealerPage = 1,
  paymentPage = 1,
  filtersReady = false,
  divisionReady = false
} = {}) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [cloading, setcLoading] = useState(false);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadedCustomerArea, setLoadedCustomerArea] = useState(null);
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingDivisions, setLoadingDivisions] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const requestIdRef = useRef(0);
  const customerRequestIdRef = useRef(0);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const pageSize = 10;

  const loadZones = useCallback(async () => {
    try {
      setLoadingZones(true);
      const response = await fetchZonesPOReports(user.userCode, 'sales');
      if (response?.status && Array.isArray(response.zones)) {
        setZoneOptions(
          response.zones.map((z, idx) => ({
            value: z.zoneCode || `${z.zoneCode}_${idx}`,
            label: z.region?.trim() || z.zoneName?.trim() || z.zoneCode,
            zoneCode: z.zoneCode
          }))
        );
      } else {
        setZoneOptions([]);
      }
    } catch (err) {
      console.error('Zone load error:', err);
      setZoneOptions([]);
    } finally {
      setLoadingZones(false);
    }
  }, [user.userCode]);

  const loadDivisions = useCallback(async () => {
    try {
      setLoadingDivisions(true);
      const response = await fetchUserDivision();
      if ((response?.success === true || response?.status === true) && Array.isArray(response.data)) {
        const mappedDivisions = response.data.map((item) => {
          if (item && typeof item === 'object' && 'value' in item && 'label' in item) {
            return item;
          }

          const value = item.divisionCode ?? item.division_code ?? item.value ?? item.divCode ?? item.divId ?? item.code ?? item.id ?? '';
          const label = item.divisionName ?? item.division_name ?? item.label ?? item.name ?? item.divName ?? value;

          return {
            value: String(value),
            label: String(label)
          };
        });

        setDivisionOptions(mappedDivisions.filter((opt) => opt.value));
      } else {
        setDivisionOptions([]);
      }
    } catch (err) {
      console.error('Division load error:', err);
      setDivisionOptions([]);
    } finally {
      setLoadingDivisions(false);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    const requestId = ++customerRequestIdRef.current;
    const zone = joinSelectedValues(area, 'zoneCode');
    setCustomerOptions([]);

    if (!zone) {
      setLoadedCustomerArea(zone);
      setLoadingCustomers(false);
      return;
    }

    try {
      setLoadingCustomers(true);
      const response = await fetchZonePartiesPOReports(user.userCode, {
        type: 'agency',
        zone
      });

      if (response?.status && Array.isArray(response.parties)) {
        if (requestId !== customerRequestIdRef.current) return;
        setCustomerOptions(response.parties.map((party) => ({
          value: String(party.partyCode || '').trim(),
          label: [party.partyName, party.city, party.partyCode].filter(Boolean).join(' - ')
        })).filter((party) => party.value));
      } else {
        if (requestId === customerRequestIdRef.current) setCustomerOptions([]);
      }
      if (requestId === customerRequestIdRef.current) setLoadedCustomerArea(zone);
    } catch (err) {
      console.error('Agency customer load error:', err);
      if (requestId === customerRequestIdRef.current) {
        setCustomerOptions([]);
        setLoadedCustomerArea(zone);
      }
    } finally {
      if (requestId === customerRequestIdRef.current) setLoadingCustomers(false);
    }
  }, [area, user.userCode]);

  const fetchDashboard = useCallback(async () => {
    if (!user?.userCode) {
      setDashboardData(null);
      return;
    }

    if (!divisionReady || !filtersReady) {
      requestIdRef.current += 1;
      setcLoading(false);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setcLoading(true);
    try {
      const json = await fetchAgencyDashboardReports({
        userCode: user.userCode,
        pageSize,
        area: joinSelectedValues(area, 'zoneCode'),
        party: zoneOptions.length === 0 ? user.userCode : joinSelectedValues(customer),
        partyFilterApplied: true,
        month: joinSelectedMonths(month),
        division: joinSelectedValues(division),
        budgetPage: 1,
        growthVolumePage: 1,
        growthValuePage: 1,
        currentQuarterPage: 1,
        lastQuarterPage: 1,
        dealerPage,
        paymentPage
      });

      if (requestId === requestIdRef.current) {
        setDashboardData(json && (json.success === true || json.success === 'true') ? json : null);
      }
    } catch (err) {
      console.error('Agency dashboard error:', err);
      if (requestId === requestIdRef.current) {
        setDashboardData(null);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setcLoading(false);
      }
    }
  }, [area, customer, dealerPage, division, divisionReady, filtersReady, month, paymentPage, user?.userCode, zoneOptions.length]);

  useEffect(() => {
    loadZones();
    loadDivisions();
  }, [loadDivisions, loadZones]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return {
    dashboardData,
    cloading,
    pageSize,
    divisionOptions,
    customerOptions,
    loadedCustomerArea,
    loadingZones,
    loadingDivisions,
    loadingCustomers,
    zoneOptions,
    refreshDashboard: fetchDashboard
  };
};

export default AgencyPerformanceReportController;
